import type { AuthProvider } from "@refinedev/core";
import type { QueryClient } from "@tanstack/react-query";
import axios, { AxiosError, type AxiosInstance } from "axios";
import {
  buildScalekitLogoutUrl,
  hasLtiTabSession,
  isLtiSessionExpired,
  markLtiTabSession,
  type AuthChallenge,
  type AuthenticationToken,
  type AuthProviderName,
  type AuthProviderToken,
  type AuthService,
  type MfaChallengeResponse,
  type MfaFactor,
} from "@libertify/auth/core";
import type { AnswerMfaResult } from "./types";
import { getSafeRedirectPath } from "./getSafeRedirectPath";

const MFA_REQUIRED_CODE: MfaChallengeResponse["code"] = "MFA_REQUIRED";

// No real error here — this is just a mid-flow redirect to the 2FA step.
// `error.name` doubles as a marker so the app's notification provider can
// suppress refine's default error toast, which it would otherwise fire
// unconditionally for any success:false (see useContextNotificationProvider).
const MFA_REDIRECT_ERROR = {
  message: "",
  name: MFA_REQUIRED_CODE,
  statusCode: 200,
};

const isMfaChallenge = (
  data: AuthenticationToken | MfaChallengeResponse
): data is MfaChallengeResponse =>
  "code" in data && data.code === MFA_REQUIRED_CODE;

/**
 * Answers the MFA challenge currently stored in `service.getMfaSession()`.
 * Standalone (not part of `AuthProvider`) because Refine has no generic
 * "answer challenge" slot — used directly by the /mfa/verify page.
 */
export const answerMfaChallenge = async (
  axiosInstance: AxiosInstance,
  service: AuthService,
  challengeResponse: string
): Promise<AnswerMfaResult> => {
  const mfaSession = service.getMfaSession();
  if (!mfaSession) {
    return { done: false, restart: true };
  }

  try {
    const response = await axiosInstance<AuthenticationToken | MfaChallengeResponse>({
      url: "/api/tokens/mfa",
      method: "post",
      data: {
        email: mfaSession.email,
        session: mfaSession.session,
        challenge: mfaSession.challenge,
        challenge_response: challengeResponse,
      },
    });

    if (!isMfaChallenge(response.data)) {
      if (!response.data.access_token) {
        service.clearMfaSession();
        return { done: false, restart: true };
      }
      service.setToken(
        response.data as Parameters<AuthService["setToken"]>[0]
      );
      service.clearMfaSession();
      return { done: true };
    }

    // Spec: always send back the latest session, even mid-flow
    // (e.g. after answering SELECT_MFA_TYPE).
    service.setMfaSession({
      email: mfaSession.email,
      session: response.data.session,
      challenge: response.data.challenge,
      message: response.data.message,
      available_factors: response.data.available_factors,
    });
    return {
      done: false,
      challenge: response.data.challenge,
      message: response.data.message,
      available_factors: response.data.available_factors,
    };
  } catch (e) {
    const error = e as Error | AxiosError;
    console.debug({ error });
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail;
      // A wrong OTP (CODE_MISMATCH) is retryable in place — same UX as
      // signup's verification step — since Cognito keeps the session alive
      // across mismatched attempts. Only a missing session (see the guard
      // above) forces a restart from /login.
      return { done: false, error: detail?.message ?? error.message };
    }
    return { done: false, error: "" };
  }
};

export interface SocialLoginToken {
  access_token: string;
  token_type: string;
  refresh_token?: string;
  id_token?: string;
}

export interface RegisterParams<TApplication = string> {
  email: string;
  password: string;
  firstname?: string;
  lastname?: string;
  invite_id?: string;
  application_id?: TApplication;
}

export interface CreateAuthProviderConfig<TUser = unknown> {
  service: AuthService;
  axiosInstance: AxiosInstance;
  apiUrl: string;
  /**
   * Getter for the QueryClient so the authProvider can be created at module
   * load time and the QueryClient injected later via `setupAuthProvider()`.
   * Mirrors admin's existing lazy-init pattern.
   */
  queryClient: () => QueryClient | null;

  i18n: {
    t: (key: string) => string;
  };

  analytics: {
    /**
     * Tracking calls are fire-and-forget — declared as `void` return so
     * call sites don't need `void` operator or `.catch(noop)` to silence
     * the floating-promise lint. Admin's `trackEventWithoutHooks` returns
     * `Promise<void>` and is assignable here via the void-return-type rule.
     */
    trackEvent(event: string, props?: object): void;
    identify(id: string, traits: Record<string, unknown>): Promise<void>;
    persistOnboardingProperties(props: Record<string, unknown>): void;
    createUserTraits(
      user: TUser,
      extras?: Record<string, unknown>
    ): Record<string, unknown>;
    toAuthMethod(provider: AuthProviderName): string;
  };

  events: {
    Login: string;
    LoginFailed: string;
    UserActivated: string;
  };

  scalekit: {
    envUrl: string;
    clientId: string;
    /**
     * Where Scalekit should send the user after the IdP terminates the
     * session. Accepts a string OR a thunk — pass a thunk when the value
     * depends on the current window origin (e.g. preview deploys, embedded
     * iframes) so it's recomputed at logout time instead of frozen at
     * authProvider-construction time.
     */
    postLogoutRedirectUri: string | (() => string);
  };

  /**
   * Called during logout, after auth state is cleared and the React Query
   * cache is flushed, but before the post-logout redirect. Use for
   * consumer-specific cleanup such as resetting Zustand stores, clearing
   * Sentry/analytics identities, removing persisted state keys, etc.
   *
   * The lib does not enforce any ordering between actions inside this hook;
   * the caller controls that.
   */
  onLogoutCleanup?(): void;

  /**
   * Determines whether a user was just created. Defaults to a 5-minute
   * threshold using ISO string parsing. Admin can override with a dayjs/tz
   * aware implementation.
   */
  isNewUser?: (createdDate: string, thresholdMinutes?: number) => boolean;
}

const defaultIsNewUser = (
  createdDate: string,
  thresholdMinutes = 5
): boolean => {
  const created = new Date(createdDate).getTime();
  const diffMinutes = (Date.now() - created) / 60_000;
  return diffMinutes < thresholdMinutes;
};

export const createAuthProvider = <
  TUser extends { id: string | number; created?: string | null } = {
    id: string | number;
    created?: string | null;
  }
>(
  config: CreateAuthProviderConfig<TUser>
): AuthProvider => {
  const {
    service,
    axiosInstance,
    apiUrl,
    queryClient: getQueryClient,
    i18n: { t },
    analytics,
    events,
    scalekit,
    onLogoutCleanup,
    isNewUser = defaultIsNewUser,
  } = config;

  // On app start, an expired LTI token outside its own Moodle tab (or being replaced by a relaunch) is dropped, so the user gets /login.
  if (
    isLtiSessionExpired(service.getToken()) &&
    (!hasLtiTabSession() ||
      new URL(globalThis.location.href).searchParams.has("token"))
  ) {
    service.clearToken();
  }

  const createAxiosHeaders = (customTokens?: AuthProviderToken) => {
    const tokens = customTokens ?? service.getToken();
    if (!tokens?.access_token) {
      return null;
    }
    return {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    };
  };

  const fetchUser = async (
    customToken?: AuthProviderToken
  ): Promise<TUser | null> => {
    const optionsHeaders = createAxiosHeaders(customToken);
    if (!optionsHeaders) return null;
    const user = (
      await axiosInstance.get<TUser>(`/api/users/me`, optionsHeaders)
    ).data;
    return user;
  };

  const exchangeTransferToken = async (
    transferToken: string,
    url: URL,
    paramName: string,
  ): Promise<{ authenticated: false; redirectTo: string; logout: true; error: { message: string; name: string } } | null> => {
    try {
      const response = await axios<{ access_token: string }>({
        url: `${apiUrl}/api/tokens/exchange`,
        method: "post",
        data: { transfer_token: transferToken },
      });
      service.setToken({
        access_token: response.data.access_token,
        token_type: "bearer",
        provider: "cognito",
        // Only the LTI launch param (not qr_id) marks the session as Moodle-originated.
        ...(paramName === "token" ? { authOrigin: "lti" as const } : {}),
      });
      if (paramName === "token") markLtiTabSession();
      return null;
    } catch (error) {
      console.error("Token exchange failed:", error);
      service.clearToken();
      return {
        authenticated: false,
        redirectTo: "/login",
        logout: true,
        error: {
          message: "Token exchange failed",
          name: "Authentication Error",
        },
      };
    } finally {
      url.searchParams.delete(paramName);
      globalThis.history.replaceState({}, "", url.toString());
    }
  };

  // ─── login helpers ──────────────────────────────────────────────────
  // Extracted from `login()` to keep its cognitive complexity below the
  // Sonar threshold. Each helper handles one of the three sub-flows
  // (social-token, email/password POST, email/password response branches).

  const SOCIAL_PROVIDERS = new Set<AuthProviderName>([
    "google",
    "scalekit",
    "apple",
  ]);

  const trackOAuthUserActivation = async (
    token: SocialLoginToken,
    provider: AuthProviderName
  ): Promise<void> => {
    try {
      const user = await fetchUser({ ...token, provider });
      if (!user) return;
      await analytics.identify(
        user.id.toString(),
        analytics.createUserTraits(user, { signup_method: provider })
      );
      if (user.created && isNewUser(user.created)) {
        analytics.trackEvent(events.UserActivated, {
          signup_method: provider,
        });
        analytics.persistOnboardingProperties({ signup_method: provider });
      }
    } catch (error) {
      console.warn("Error tracking OAuth user registration:", error);
    }
  };

  const handleSocialLogin = async (
    provider: AuthProviderName,
    token: SocialLoginToken,
    redirect: boolean
  ) => {
    try {
      service.setToken({ ...token, provider });
      service.clearMfaSession();
      await trackOAuthUserActivation(token, provider);
      analytics.trackEvent(events.Login, {
        login_method: analytics.toAuthMethod(provider),
      });
      return redirect
        ? { success: true, redirectTo: "/" }
        : { success: true };
    } catch {
      analytics.trackEvent(events.LoginFailed, {
        login_method: analytics.toAuthMethod(provider),
        reason: "oauth_error",
      });
      return {
        success: false,
        error: {
          message: t("src.authProvider.socialLoginError"),
          name: t("src.authProvider.invalidSocialLogin"),
        },
      };
    }
  };

  const invalidCredsResponse = (messageKey: string, nameKey: string) => ({
    success: false as const,
    error: { message: t(messageKey), name: t(nameKey) },
  });

  const handleEmailLoginResponse = (
    // `data` is the body returned by `POST /api/tokens` — typed loosely
    // because the lib doesn't own the backend's response schema. The MFA
    // challenge fields (code/session/challenge/...) come back flat on a 200,
    // discriminated from AuthenticationToken by `code === "MFA_REQUIRED"`;
    // the other error codes below still come nested under `detail` on a 4xx.
    data: {
      access_token?: string;
      code?: string;
      session?: string;
      challenge?: AuthChallenge;
      message?: string;
      available_factors?: MfaFactor[];
      detail?: { code?: string };
    },
    email: string,
    redirect: boolean
  ) => {
    if (data.access_token) {
      service.setToken(
        data as Parameters<AuthService["setToken"]>[0]
      );
      analytics.trackEvent(events.Login, { login_method: "email" });
      return { success: true, redirectTo: redirect ? "/" : undefined };
    }

    if (
      data.code === MFA_REQUIRED_CODE &&
      data.challenge === "NEW_PASSWORD_REQUIRED" &&
      data.session
    ) {
      service.setMfaSession({
        email,
        session: data.session,
        challenge: data.challenge,
      });
      return {
        success: false as const, // necessary for the redirect to work
        redirectTo: "/change-password",
        error: {
          message: t("src.authProvider.youMustChange"),
          name: t("src.authProvider.login"),
        },
      };
    }

    if (data.code === MFA_REQUIRED_CODE && data.challenge && data.session) {
      service.setMfaSession({
        email,
        session: data.session,
        challenge: data.challenge,
        message: data.message,
        available_factors: data.available_factors,
      });
      // Refine's useLogin only forwards the URL's ?to= deep-link param when
      // login() returns success:true, so it's carried manually from here on.
      const to = new URLSearchParams(globalThis.location.search).get("to");
      return {
        success: false as const,
        redirectTo: to
          ? `/mfa/verify?to=${encodeURIComponent(getSafeRedirectPath(to))}`
          : "/mfa/verify",
        error: MFA_REDIRECT_ERROR,
      };
    }

    const code = data.detail?.code;

    if (code === "EMAIL_UNVERIFIED") {
      return {
        success: true,
        // pass in base64 in url, from=login to auto-resend code
        redirectTo: "/verification?email=" + btoa(email) + "&from=login",
      };
    }

    if (code === "ACCOUNT_LOCKED") {
      analytics.trackEvent(events.LoginFailed, {
        login_method: "email",
        reason: "invalid_credentials",
      });
      return invalidCredsResponse(
        "src.authProvider.accountLocked",
        "src.authProvider.login"
      );
    }

    const reason = code === "INVALID_CREDENTIALS" ? "invalid_credentials" : "unknown";
    analytics.trackEvent(events.LoginFailed, {
      login_method: "email",
      reason,
    });
    return invalidCredsResponse(
      "src.authProvider.loginError",
      "src.authProvider.invalidEmailOr"
    );
  };

  const authProvider: AuthProvider = {
    login: async ({
      email,
      password,
      redirect = true,
      provider = "cognito",
      token,
    }: {
      email?: string;
      password?: string;
      redirect?: boolean;
      provider?: AuthProviderName;
      token?: SocialLoginToken;
    }) => {
      if (SOCIAL_PROVIDERS.has(provider) && token) {
        return handleSocialLogin(provider, token, redirect);
      }

      if (!email || !password) {
        return invalidCredsResponse(
          "src.authProvider.emailAndPasswordRequired",
          "src.authProvider.invalidLogin"
        );
      }

      // axiosInstance has baseURL = apiUrl, so /api/tokens resolves to the
      // same absolute URL. Routing through the instance also gives login the
      // maintenance-mode (503) interceptor that bare axios bypassed.
      const response = await axiosInstance({
        url: "/api/tokens",
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        data: new URLSearchParams({
          username: email.toLowerCase(),
          password,
        }),
        // accept 400 errors (don't reject the axios promise) to process use cases
        validateStatus: (status) => status >= 200,
      });

      return handleEmailLoginResponse(response.data, email, redirect);
    },
    logout: async () => {
      // Snapshot the token BEFORE any clearing so it's available for the
      // DELETE auth header AND the post-logout Scalekit SLO branch even
      // though storage gets wiped concurrently below.
      const preLogoutToken = service.getToken();

      // Three operations fired in parallel:
      //   1. DELETE /api/tokens (backend-side revocation, required by security audit)
      //   2. Cancel in-flight React Query queries (prevents post-logout stale-token completions)
      //   3. service.logout() — wipes storage, notifies subscribers, awaits per-provider OAuth handler
      //      (the handler waits for the DELETE, since it may redirect away and abort it)
      const deleteTokens = (async () => {
        try {
          if (preLogoutToken?.access_token) {
            await axiosInstance({
              url: "/api/tokens",
              method: "delete",
              headers: {
                Authorization: `Bearer ${preLogoutToken.access_token}`,
              },
              // Social logins have no refresh_token here — Amplify revokes it in its logout handler
              ...(preLogoutToken.refresh_token && {
                data: { refresh_token: preLogoutToken.refresh_token },
              }),
            });
          }
        } catch (error) {
          // refresh_token already expired → ignore; storage is wiped regardless
          console.warn("Error deleting auth tokens", error);
        }
      })();

      const [, , { token }] = await Promise.all([
        deleteTokens,
        getQueryClient()?.cancelQueries() ?? Promise.resolve(),
        service.logout({ waitBeforeProviderLogout: deleteTokens }),
      ]);

      // Clear React Query cache to prevent stale data persisting to next user.
      getQueryClient()?.clear();

      // Consumer-controlled cleanup hook (Zustand reset, Sentry, analytics
      // super-properties, persisted state keys, …). Runs synchronously so the
      // caller can guarantee ordering against the Scalekit redirect below.
      onLogoutCleanup?.();

      // Scalekit SLO: redirect to IdP end_session endpoint to terminate the
      // IdP session. Scalekit redirects back to /login via
      // post_logout_redirect_uri. Resolve the redirect URI lazily so it
      // reflects the CURRENT window origin (preview deploys, iframe embeds).
      if (token?.provider === "scalekit") {
        const postLogoutRedirectUri =
          typeof scalekit.postLogoutRedirectUri === "function"
            ? scalekit.postLogoutRedirectUri()
            : scalekit.postLogoutRedirectUri;
        globalThis.location.href = buildScalekitLogoutUrl({
          envUrl: scalekit.envUrl,
          clientId: scalekit.clientId,
          postLogoutRedirectUri,
          idTokenHint: token.id_token,
        });
        return { success: true };
      }

      // LTI sessions have no /login of their own — send them back to Moodle instead.
      if (token?.authOrigin === "lti") {
        return {
          success: true,
          redirectTo: "/lti/session-expired",
        };
      }

      // Amplify is already redirecting through Cognito /logout; a Refine redirect could cancel it
      if (token?.provider === "google") {
        return { success: true };
      }

      return {
        success: true,
        redirectTo: "/login",
      };
    },
    onError: async (error) => {
      const isRefreshTokenIssue = [
        "Invalid Refresh Token",
        "Refresh Token has been revoked",
        "Refresh Token has expired",
      ].includes(error.message);

      if (error.status === 400 && isRefreshTokenIssue) {
        return {
          logout: true,
          redirectTo: "/login",
        };
      }
      return {};
    },

    check: async () => {
      const url = new URL(globalThis.location.href);
      const qrId = url.searchParams.get("qr_id");
      if (qrId) {
        // Clear any stale token so the next check() doesn't re-authenticate
        // the user under the previous identity (the intent of the qr_id
        // flow is to swap identities). Also scrub qr_id from the URL so a
        // window-focus refetch doesn't replay the same failing exchange.
        const exchangeError = await exchangeTransferToken(qrId, url, "qr_id");
        if (exchangeError) return exchangeError;
      }

      const ltiToken = url.searchParams.get("token");
      if (ltiToken) {
        const exchangeError = await exchangeTransferToken(ltiToken, url, "token");
        if (exchangeError) return exchangeError;
      }

      const token = service.getToken();

      // Expired LTI tokens can't be refreshed — send the teacher back to Moodle.
      if (isLtiSessionExpired(token)) {
        return {
          authenticated: false,
          redirectTo: "/lti/session-expired",
        };
      }

      if (token?.access_token) {
        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        redirectTo: "/login",
        logout: true,
        error: {
          message: t("src.authProvider.checkFailed"),
          name: t("src.authProvider.userNotFound"),
        },
      };
    },
    getPermissions: async () => {
      /* no-op */
    },
    getIdentity: async () => {
      const tokens = service.getToken();
      if (!tokens?.access_token) {
        return null;
      }

      const user = await fetchUser();
      if (!user) return null;

      return {
        ...user,
        token: tokens,
      };
    },
    register: async ({
      email,
      password,
      firstname = "",
      lastname = "",
      invite_id,
      application_id,
    }: RegisterParams) => {
      const payload: Record<string, unknown> = {
        email,
        password,
        firstname,
        lastname,
        ...(invite_id && { invite_id }),
        ...(application_id && { application_id }),
      };

      try {
        const response = await axiosInstance({
          url: "/api/users/creation_request",
          method: "post",
          data: payload,
          validateStatus: function (status) {
            // accept 409 errors (don't reject the axios promise) to process use cases
            return status >= 200;
          },
        });
        if (response.status === 200) {
          const token = response.data.access_token;
          if (token) {
            const user = await fetchUser({
              access_token: token,
              token_type: "bearer",
              provider: "cognito",
            });

            if (user) {
              await analytics.identify(
                user.id.toString(),
                analytics.createUserTraits(user, { signup_method: "email" })
              );
              analytics.persistOnboardingProperties({
                signup_method: "email",
              });
            }
          }
        }

        if (response.status === 200) {
          return {
            success: true,
            redirectTo: "/verification?email=" + btoa(email),
          };
        }

        if ([409, 401].includes(response.status)) {
          return {
            success: false,
            error: {
              name: t("src.authProvider.registrationError"),
              message:
                response.data.detail?.message ||
                t("src.authProvider.userAlreadyExists"),
            },
          };
        }

        if (response.status === 400) {
          return {
            success: false,
            error: {
              name: t("src.authProvider.registrationError"),
              message:
                response.data.detail?.message ||
                t("src.authProvider.validationError"),
            },
          };
        }

        return {
          success: false,
          error: {
            name: t("src.authProvider.registrationError"),
            message:
              response.data.detail?.message ||
              t("src.authProvider.unexpectedError"),
          },
        };
      } catch (error) {
        console.error("Registration error:", error);

        if (axios.isAxiosError(error)) {
          return {
            success: false,
            error: {
              name: t("src.authProvider.registrationError"),
              message:
                error.response?.data?.detail?.message ||
                error.message ||
                t("src.authProvider.networkError"),
            },
          };
        }

        return {
          success: false,
          error: {
            name: t("src.authProvider.registrationError"),
            message: t("src.authProvider.unexpectedError"),
          },
        };
      }
    },
    updatePassword: async ({ password }) => {
      const mfaSession = service.getMfaSession();
      if (!mfaSession) {
        return {
          success: false,
          error: {
            name: t("src.authProvider.invalidPasswordChange"),
            message: "MFA session not found",
          },
        };
      }

      const result = await answerMfaChallenge(axiosInstance, service, password);

      if (result.done) {
        analytics.trackEvent(events.Login, { login_method: "email" });
        return { success: true, redirectTo: "/" };
      }

      if ("restart" in result) {
        return {
          success: false,
          redirectTo: "/login",
          error: {
            name: t("src.authProvider.invalidPasswordChange"),
            message: t("src.authProvider.passwordChangeSessionExpired"),
          },
        };
      }

      if ("challenge" in result) {
        // Account also has MFA enabled — password changed, now answer the
        // next challenge on the dedicated verify page.
        return {
          success: false,
          redirectTo: "/mfa/verify",
          error: MFA_REDIRECT_ERROR,
        };
      }

      return {
        success: false,
        error: {
          name: t("src.authProvider.invalidPasswordChange"),
          message: result.error,
        },
      };
    },
  };

  return authProvider;
};
